import React, { useRef, useEffect, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Animated,
  Alert,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { useSelectedLocation } from "../../hooks/useSelectedLocation";
import { resetPulseCheck } from "../../store/slices/pulseCheckSlice";
import ValidationErrorBanner from "./ValidationErrorBanner";

import PulseCheckHeader from "./PulseCheckHeader";
import PulseCheckActivity from "./PulseCheckActivity";
import PulseCheckQuestions from "./PulseCheckQuestions";
import PulseFirstCheck from "./PulseFirstCheck";
import PulseSecondCheck from "./PulseSecondCheck";
import PulseCheckAction from "./PulseCheckAction";
import PulseCheckMiscellaneousObservations from "./PulseCheckMiscellaneousObservations";
import PulseCheckSignature from "./PulseCheckSignature";
import LHLoader from "../../components/LHLoader";
import { fetchQuestions } from "./fetchQuestionsUtil";
import { validateForm } from "./validateFormUtil";
import PulseCheckNotes from "./PulseCheckNotes";
import ObservationErrorModal from "../observation/components/ObservationErrorModal";
import NhoErrorModal from '../NewHireOnboarding/components/NhoErrorModal';
import { useTheme } from "../../theme";

interface PulseCheckScreenProps {
  onBackPress: () => void;
  onHomePress?: () => void; // Add optional home navigation handler
  forceDisabled?: boolean;
}

const PulseCheckScreen: React.FC<PulseCheckScreenProps> = ({
  onBackPress,
  onHomePress,
  forceDisabled = false,
}) => {
  console.log("PulseCheckScreen rendered");
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const dispatch = useDispatch();

  // Get selected filters from Redux
  const selectedFilters = useSelector(
    (state: RootState) => state.observation.selectedFilters
  );

  // Get selected record and pulse state from Redux
  const selectedRecord = useSelector(
    (state: RootState) => state.pulseCheck.selectedRecord
  );

  const pulseState = useSelector(
    (state: RootState) => state.pulseCheck.pulseState
  );

  console.log("PulseCheck access with state:", pulseState);

  // Get selected location for location-change detection
  const selectedLocation = useSelectedLocation();

  // Safe ref pattern: redirect to Observation Dashboard on location change
  const initialLocationIdRef = useRef(selectedLocation?.id);
  const onBackPressRef = useRef(onBackPress);
  useEffect(() => { onBackPressRef.current = onBackPress; }, [onBackPress]);
  const hasRedirectedRef = useRef(false);

  // Detect location changes and redirect back to Observation Dashboard
  useEffect(() => {
    const initialId = initialLocationIdRef.current;
    if (
      !hasRedirectedRef.current &&
      initialId &&
      selectedLocation?.id &&
      initialId !== selectedLocation.id
    ) {
      hasRedirectedRef.current = true;
      dispatch(resetPulseCheck());
      onBackPressRef.current();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id]);

  const formValues = useSelector(
    (state: RootState) => state.pulseCheck.formValues
  );
  const progress = useRef(new Animated.Value(0.4)).current;
  const scrollViewRef = useRef<ScrollView>(null);
  
  // State for scroll to top button
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollButtonOpacity = useRef(new Animated.Value(0)).current;

  // Use fetchedQuestions from Redux store instead of local state
  const fetchedQuestions = useSelector(
    (state: RootState) => state.pulseCheck.fetchedQuestions
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Reusable function to scroll to the top of the application container
  const scrollToTop = () => {
    try {
      // For web platform, scroll to top of document
      if (Platform.OS === "web") {
        // @ts-ignore - Access to window object in web platform
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      }
      // For native platforms, use the scrollView reference
      else if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: 0,
          y: 0,
          animated: true,
        });
      }
    } catch (scrollError) {
      // Renamed variable to avoid shadowing
      console.error("Error scrolling to top:", scrollError);
    }
  };

  // Handle scroll events to show/hide scroll to top button
  const handleScroll = (event: any) => {
    const { contentOffset } = event.nativeEvent;
    const shouldShow = contentOffset.y > 200; // Show button after scrolling 200px
    
    if (shouldShow !== showScrollToTop) {
      setShowScrollToTop(shouldShow);
      
      Animated.timing(scrollButtonOpacity, {
        toValue: shouldShow ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  };

  // Handle scroll events for web
  useEffect(() => {
    if (Platform.OS === "web") {
      const handleWebScroll = () => {
        const shouldShow = window.scrollY > 200;
        
        if (shouldShow !== showScrollToTop) {
          setShowScrollToTop(shouldShow);
          
          Animated.timing(scrollButtonOpacity, {
            toValue: shouldShow ? 1 : 0,
            duration: 200,
            useNativeDriver: true,
          }).start();
        }
      };

      window.addEventListener('scroll', handleWebScroll);
      return () => window.removeEventListener('scroll', handleWebScroll);
    }
  }, [showScrollToTop, scrollButtonOpacity]);

  useEffect(() => {
    // Call fetchQuestions function from the utility file
    fetchQuestions(
      {
        role: selectedFilters.role,
        observationType: selectedFilters.observationType,
      },
      pulseState,
      selectedRecord,
      dispatch,
      setError,
      setLoading
    );
    // Run on mount, role/observationType changes, or when pulseState/selectedRecord changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFilters.role,
    selectedFilters.observationType,
    pulseState,
    selectedRecord?.id,
  ]);

  // Enhanced function to display status messages and scroll to app container
  const showStatusMessage = (type: "success" | "error", message: string) => {
    setStatusMessage({ type, message });

    // Use timeout to ensure the message is rendered before scrolling
    setTimeout(() => {
      scrollToTop();
    }, 100);

    // Auto-hide success messages after 5 seconds, keep error messages visible until resolved
    if (type === "success") {
      setTimeout(() => {
        setStatusMessage(null);
      }, 5000);
    }
  };

  // validateForm function has been moved to a separate utility file

  // Wrapper function for the extracted validateForm utility
  const handleValidateForm = (): boolean => {
    // Use type assertion to handle the form values type safely
    const formValuesCasted = formValues as unknown as Record<string, string>;
    return validateForm(
      formValuesCasted,
      dispatch,
      showStatusMessage,
      scrollToTop,
      progress
    );
  };

  // Helper to render PulseCheckActivity for each section
  const renderActivitySection = (sectionKey: string) => {
    if (
      !fetchedQuestions ||
      !fetchedQuestions[sectionKey] ||
      fetchedQuestions[sectionKey].length === 0
    ) {
      return null;
    }
    return (
      <PulseCheckActivity
        key={sectionKey}
        title={sectionKey}
        options={fetchedQuestions[sectionKey].map((q: any) => ({
          id: q.ID?.toString() || q.Id?.toString() || q.id?.toString(), // Use ID directly as observationId
          label: q.Observation || q.Question,
          category: q.Category || sectionKey, // Use sectionKey as category if not available
          SPARC_Category: q.SPARC_Category, // Include SPARC_Category from backend
          Material: q.Material, // Include Material field for PDF link
          Pallet_Type: q.Pallet_Type, // Include Pallet_Type for second label
        }))}
      />
    );
  };

  if (loading) {
    return (
      <>
        <View style={[styles.container, { backgroundColor: colors.background.page }]}>
          <PulseCheckHeader
            title={`${selectedFilters.observationType}: ${selectedFilters.employee}`}
            subtitle={`${selectedFilters.shift} Shift- ${selectedFilters.role}`}
            logoSource={require("../../assets/warehouse_navigator.png")}
            onReturn={onBackPress}
            onHomePress={onHomePress}
          />
          <LHLoader />
        </View>
        <NhoErrorModal />
        <ObservationErrorModal />
      </>
    );
  }

  if (error) {
    return (
      <>
        <View style={[styles.pulseErrorViewContainer, { backgroundColor: colors.background.page }] }>
          <Text style={[styles.pulseErrorText, { color: colors.status.error }]}>{error}</Text>
        </View>
        <NhoErrorModal />
        <ObservationErrorModal />
      </>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={[styles.keyboardView, { backgroundColor: colors.background.page }]}
      keyboardVerticalOffset={Platform.OS === "ios" ? 64 : 0}
    >
      <ScrollView
        ref={scrollViewRef}
        style={{ backgroundColor: colors.background.page }}
        contentContainerStyle={[styles.container, { backgroundColor: colors.background.page }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={true}
        bounces={false}
        overScrollMode="always"
        onScroll={Platform.OS !== "web" ? handleScroll : undefined}
        scrollEventThrottle={16}
      >
        <View style={styles.innerContainer}>
          {statusMessage && (
            <View style={styles.statusMessageContainer}>
              {statusMessage.type === "error" ? (
                <ValidationErrorBanner
                  errorMessage={statusMessage.message}
                  onPress={scrollToTop}
                />
              ) : (
                <View style={[styles.statusMessage, styles.successMessage]}>
                  <MaterialIcons
                    name="check-circle"
                    size={20}
                    color="#155724"
                    style={styles.messageIcon}
                  />
                  <Text style={styles.successText}>
                    {statusMessage.message}
                  </Text>
                </View>
              )}
            </View>
          )}
          <PulseCheckHeader
            title={`${selectedFilters.observationType}: ${selectedFilters.employee}`}
            subtitle={`${selectedFilters.shift} Shift- ${selectedFilters.role}`}
            logoSource={require("../../assets/warehouse_navigator.png")}
            onReturn={onBackPress}
            onHomePress={onHomePress}
          />
          {renderActivitySection("Before Activity")}
          {renderActivitySection("During Activity (PIT)")}
          {renderActivitySection("During Activity (Ergo)")}
          {renderActivitySection("During Activity (Process)")}
          {renderActivitySection("After Activity")}
          {fetchedQuestions && fetchedQuestions["Reference Question"] && (
            <PulseCheckQuestions
              questions={fetchedQuestions["Reference Question"].map(
                (q: any) => ({
                  id: q.ID?.toString() || q.Id?.toString() || q.id?.toString(), // Use ID directly as questionId
                  label: q.Question,
                })
              )}
            />
          )}
          {selectedFilters.observationType === "Pulse Check" && (
            <PulseFirstCheck />
          )}
          {selectedFilters.observationType === "Pulse Check" && (
            <PulseSecondCheck />
          )}
          {selectedFilters.observationType !== "Pulse Check" && (
            <PulseCheckNotes />
          )}
          <PulseCheckMiscellaneousObservations />
          {/* Always render signature pad for Pulse Check */}
          {selectedFilters.observationType === "Pulse Check" && (
            <PulseCheckSignature />
          )}
          {selectedFilters.observationType !== "Pulse Check" && (
            <PulseCheckSignature />
          )}
          <PulseCheckAction
            onSave={() => {
              Alert.alert("Saved");
              showStatusMessage("success", "Progress saved successfully");
            }}
            onComplete={handleValidateForm}
            onBackToObservation={onBackPress}
            onShowMessage={showStatusMessage}
            forceDisabled={forceDisabled}
          />
        </View>
        {/* <Text style={styles.footer}>
          © 2025 PepsiCo | Pulse Check System v1.2
        </Text> */}
      </ScrollView>

      {/* Scroll to Top Button */}
      <Animated.View
        style={[
          styles.scrollToTopButton,
          {
            opacity: scrollButtonOpacity,
            transform: [
              {
                scale: scrollButtonOpacity.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.8, 1],
                }),
              },
            ],
          },
        ]}
        pointerEvents={showScrollToTop ? "auto" : "none"}
      >
        <TouchableOpacity
          style={[
            styles.scrollToTopTouchable,
            isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default },
          ]}
          onPress={scrollToTop}
          activeOpacity={0.7}
        >
          {/* Arrow up icon */}
          <View style={styles.arrowIcon}>
            <Text style={[styles.arrowText, isDark && { color: colors.brand.primary }]}>↑</Text>
          </View>
        </TouchableOpacity>
      </Animated.View>
      
      {/* Error Modal */}
      <NhoErrorModal />
      <ObservationErrorModal />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
  },
  container: {
    backgroundColor: "transparent",
    paddingBottom: 50,
    flexGrow: 1,
    alignItems: "stretch",
    paddingHorizontal: Platform.OS === "web" ? "2%" : 16,
  },
  innerContainer: {},
  footer: {
    textAlign: "center",
    marginTop: 40,
    color: "#868e96",
    fontSize: 12,
  },
  statusMessageContainer: {
    width: "100%",
    marginBottom: 8,
    zIndex: 10,
  },
  statusMessage: {
    padding: 14,
    borderRadius: 6,
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    width: "100%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  successMessage: {
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
  },
  errorMessage: {
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderLeftWidth: 5,
    borderLeftColor: "#dc3545",
  },
  successText: {
    color: "#155724",
    fontSize: 14,
    flex: 1,
  },
  errorText: {
    color: "#721c24",
    fontSize: 14,
    flex: 1,
  },
  messageIcon: {
    marginRight: 8,
  },
  pulseErrorViewContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  pulseErrorText: {
    color: "red",
    fontWeight: "bold",
  },
  scrollToTopButton: {
    position: "absolute",
    bottom: Platform.OS === "ios" ? 30 : 30,
    right: 20,
    zIndex: 1000,
  },
  scrollToTopTouchable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 28,
    width: 56,
    height: 56,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  arrowIcon: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  arrowText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#3582E6",
    lineHeight: 24,
  },
});
export default PulseCheckScreen;
